import type {
  AdsReport,
  DemandInsights,
  DemandReport,
  HoursReport,
  UsageReport,
  AutoTargetReport,
  BalancePage,
  BalanceRow,
  ClientDetail,
  ClientPage,
  AutoVerifyReceipt,
  DashboardStats,
  LoginRequest,
  PaymentRequest,
  ReservationPage,
  ReservationRow,
  SessionUser,
  StaffMember,
  Stats,
  TransferQueues,
  ArrivalsResponse,
  TransferRow,
  ZoneCars,
  DashboardOutlook,
  ProtocolData,
  ReservationJournal,
  ArriveRequest,
  CreateReceipt,
  CreateRequest,
  PrepaidRequest,
  ReceiptData,
  CreditNoteData,
  CreditNoteKind,
  RescheduleReceipt,
  RescheduleRequest,
  StatusRequest,
  PricelistSummary,
  Quote,
  QuoteQuery,
  ServiceCatalogItem,
  CloseShiftReceipt,
  CloseShiftRequest,
  DepartRequest,
  DispatchRequest,
  EditRequest,
  ServicesRequest,
  ServicesReceipt,
  ReservationServices,
  KeycafeBookingView,
  CapacitySetting,
  CapacityUpdate,
  AccountUpdate,
  ClubPointCreate,
  ClubPointRow,
  Closure,
  ClosureUpdate,
  CommissionUpdate,
  PasswordChange,
  PeriodCreate,
  PricelistCreate,
  PricelistFull,
  PricelistUpdate,
  PricingBand,
  PricingBandsUpdate,
  PricingConfig,
  PricingDiscount,
  PricingDiscountUpdate,
  PricingLead,
  PricingLeadsUpdate,
  PricingSeason,
  PricingSeasonCreate,
  PricingSeasonUpdate,
  PricingSettings,
  PricingSettingsUpdate,
  UserCreate,
  UserCreated,
  UserDetail,
  UserList,
  UserUpdate,
  Report,
  ReportKind,
  SoldReport,
  ServiceCatalogFull,
  ServiceUpdate,
  InternalMessage,
  LprEventRow,
  MessageCreate,
  WriteReceipt,
} from '@smile/shared';
import { apiErrorMessage } from '../lib/errors';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    /** Seconds, from the body of a 429. Undefined on every other error. */
    public retryAfter?: number,
    /**
     * A refused write says why in the person's language — "Vůz už je označen
     * jako přijatý." — and that sentence is better than anything a table of
     * codes on this side could say. Present on write refusals only.
     */
    public serverMessage?: string,
  ) {
    /* `message` is what the six screens print, so it is the Czech phrase;
       `code` stays the machine string, which is what Login switches on and
       what a log is searched by. They were the same value until a person had
       to read one of them. */
    super(apiErrorMessage(code, retryAfter));
    this.name = 'ApiError';
  }
}

/**
 * A mutation gets an operation id, once per press, and keeps it across its
 * own retries — and across the person's next press of the same thing when
 * the first ended uncertain.
 *
 * The server stores the id with the change (packages/api/src/write/opid.ts),
 * bound to who sent it and what the request was, and keeps the answer. So a
 * request that was saved but whose answer never came back can be sent again
 * without doing the thing twice: the server hands back the first answer.
 *
 * Retried here: no answer at all (the browser's "Failed to fetch"), a
 * gateway that could not reach the API (502, 504), the API's own
 * "uncertain" (503 — the database may have saved it), and a success whose
 * body could not be read. Not retried: anything else the server said; those
 * are answers. The id is remembered (this tab, 30 minutes) from before the
 * first send until an answer settles it, so pressing the same button again
 * — after retries ran out, or after a reload in the middle — re-sends the
 * same id, and the server either finishes the operation or says it already
 * did — never twice (second reviews F-04 of 6 October, F-05 of 7 October;
 * third review F-01).
 */
const RETRY_DELAYS_MS = [700, 1500];
/** v2: keys are fingerprints. Earlier formats lived under `smile.pendingOps` (see `upgradeSync`). */
const PENDING_KEY = 'smile.pendingOps.v2';
const LEGACY_PENDING_KEY = 'smile.pendingOps';
const OWNER_KEY = 'smile.pendingOwner';
const PENDING_TTL_MS = 30 * 60_000;
const FINGERPRINT_RE = /^(?:[0-9a-f]{64}|fnv-[0-9a-f]{1,16})$/;
const OP_ID_RE = /^[A-Za-z0-9-]{8,64}$/;
const isMutation = (init?: RequestInit) => !!init?.method && init.method !== 'GET' && init.method !== 'HEAD';
const newOpId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

/**
 * Requests whose body carries a password. Their pending entry is keyed by
 * method and path alone, so nothing derived from the password is kept
 * anywhere (third review F-04). A second, different password change while
 * the first is unresolved re-uses its id and is refused as reused — the
 * person reloads and starts again, which is right for a password.
 */
const SECRET_BODY_PATHS = new Set(['/api/account/password']);

/**
 * What a pending entry is filed under: a fingerprint of the request, never
 * the request (third review F-04 — the body used to be the key, so an
 * unresolved password change left both passwords in sessionStorage).
 * SHA-256 where the browser offers it (any https page, localhost); a
 * 64-bit FNV-1a otherwise, which is enough to tell two requests apart and
 * is only ever applied to bodies without a secret in them.
 */
async function fingerprint(method: string, path: string, body: string): Promise<string> {
  const text = SECRET_BODY_PATHS.has(path) ? `${method} ${path}` : `${method} ${path}\n${body}`;
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
      return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    /* fall through */
  }
  let h = 0xcbf29ce484222325n;
  for (let i = 0; i < text.length; i++) {
    h ^= BigInt(text.charCodeAt(i));
    h = (h * 0x100000001b3n) & 0xffffffffffffffffn;
  }
  return `fnv-${h.toString(16)}`;
}

/*
 * Pending operations: fingerprint -> id. Written BEFORE the first send and
 * removed only on an answer that ends the operation (`settles` below) —
 * never because an attempt was refused (fourth review R4-F-01, fifth
 * review R5-F-01). A reload, a closed laptop or
 * a lapsed login while the request is out therefore leaves the id behind,
 * and the next press of the same thing re-sends it. sessionStorage first; a
 * copy in memory for a tab where storage is off. Only well-formed entries
 * are ever read back: anything else is dropped, not re-saved (R4-F-04).
 */
type Pending = Record<string, { opId: string; at: number }>;
let memPending: Pending = {};
function wellFormed(raw: unknown): Pending {
  const out: Pending = {};
  if (!raw || typeof raw !== 'object') return out;
  const now = Date.now();
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const e = v as { opId?: unknown; at?: unknown } | null;
    if (!FINGERPRINT_RE.test(k) || !e || typeof e.opId !== 'string' || !OP_ID_RE.test(e.opId)) continue;
    if (typeof e.at !== 'number' || !Number.isFinite(e.at) || now - e.at > PENDING_TTL_MS) continue;
    out[k] = { opId: e.opId, at: e.at };
  }
  return out;
}
function readPending(): Pending {
  let stored: Pending = {};
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (raw) stored = wellFormed(JSON.parse(raw));
  } catch {
    /* storage off or unreadable: the in-memory copy stands */
  }
  return { ...wellFormed(memPending), ...stored };
}
function writePending(p: Pending): void {
  memPending = p;
  try {
    if (Object.keys(p).length === 0) sessionStorage.removeItem(PENDING_KEY);
    else sessionStorage.setItem(PENDING_KEY, JSON.stringify(p));
  } catch {
    /* private mode or storage off: the in-memory copy is all there is */
  }
}
/** Forget `key` — only if it is still this operation's (R4-F-02: an older request must not settle a newer one). */
function settle(key: string, opId: string): void {
  const p = readPending();
  if (p[key]?.opId === opId) {
    delete p[key];
    writePending(p);
  }
}
/** Keep `key` unresolved — unless a newer operation already holds it. */
function keep(key: string, opId: string): void {
  const p = readPending();
  if (!p[key] || p[key]!.opId === opId) {
    p[key] = { opId, at: Date.now() };
    writePending(p);
  }
}

/*
 * Earlier formats under the old name `smile.pendingOps` (fifth review
 * R5-F-02): the 7 October client's raw-request keys — for a password change,
 * both passwords (third review F-04) — and the next client's fingerprint
 * keys. On load, before any await, in an order where an interruption at any
 * step loses nothing:
 *   1. fingerprint-keyed entries go straight into v2 (they are already v2);
 *   2. raw non-secret entries go into a journal (`…migrating`: method, path,
 *      body, id — no password route is ever written there);
 *   3. only then is the old map removed — with its password entries, whose
 *      outcome is checked by hand (GO-LIVE §6).
 * The journal is then fingerprinted into v2 and removed. A load that finds a
 * journal (a tab closed mid-way) finishes it. If storage refuses the writes,
 * the old map is removed all the same (no password text is left behind for
 * the sake of an id) and the entries live in this page's memory.
 * Every request waits for this.
 */
const JOURNAL_KEY = 'smile.pendingOps.migrating';
type Journal = Array<{ method: string; path: string; body: string; opId: string; at: number }>;
let memJournal: Journal = [];
function readJournal(): Journal {
  try {
    const raw = sessionStorage.getItem(JOURNAL_KEY);
    const j = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(j) ? (j as Journal) : [];
  } catch {
    return memJournal;
  }
}
function upgradeSync(): void {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(LEGACY_PENDING_KEY);
  } catch {
    return;
  }
  if (raw === null) return;
  let old: Record<string, { opId?: unknown; at?: unknown }> = {};
  try {
    old = (JSON.parse(raw) as typeof old) ?? {};
  } catch {
    old = {};
  }
  raw = null;
  const now = Date.now();
  const direct: Pending = {};
  const journal: Journal = readJournal();
  for (const [k, v] of Object.entries(old)) {
    if (!v || typeof v.opId !== 'string' || !OP_ID_RE.test(v.opId)) continue;
    if (typeof v.at !== 'number' || now - v.at > PENDING_TTL_MS) continue;
    if (FINGERPRINT_RE.test(k)) {
      direct[k] = { opId: v.opId, at: v.at };
      continue;
    }
    const m = /^(\S+) (\S+) ([\s\S]*)$/.exec(k);
    if (!m || SECRET_BODY_PATHS.has(m[2]!)) continue;
    journal.push({ method: m[1]!, path: m[2]!, body: m[3]!, opId: v.opId, at: v.at });
  }
  old = {};
  memJournal = journal;
  writePending({ ...direct, ...readPending() });
  try {
    if (journal.length > 0) sessionStorage.setItem(JOURNAL_KEY, JSON.stringify(journal));
  } catch {
    /* kept in memory */
  }
  try {
    sessionStorage.removeItem(LEGACY_PENDING_KEY);
  } catch {
    /* nothing more can be done */
  }
}
const upgraded: Promise<void> = (async () => {
  upgradeSync();
  const journal = readJournal();
  if (journal.length === 0) return;
  const carried: Pending = {};
  const now = Date.now();
  for (const j of journal) {
    if (!j || typeof j.opId !== 'string' || !OP_ID_RE.test(j.opId) || typeof j.at !== 'number' || now - j.at > PENDING_TTL_MS) continue;
    if (typeof j.method !== 'string' || typeof j.path !== 'string' || typeof j.body !== 'string' || SECRET_BODY_PATHS.has(j.path)) continue;
    carried[await fingerprint(j.method, j.path, j.body)] = { opId: j.opId, at: j.at };
  }
  writePending({ ...carried, ...readPending() });
  memJournal = [];
  try {
    sessionStorage.removeItem(JOURNAL_KEY);
  } catch {
    /* storage off */
  }
})();

/*
 * Whose unresolved operations these are. Set from the session the server
 * confirms — `/api/auth/me` as well as a login (R4-F-01: a tab that never
 * signed in through this client still has an owner). Entries with no owner
 * yet are taken over by the first confirmed person: the server binds every
 * id to its user, so a wrong guess costs one "op_id_reused", never a
 * second change. A different person clears them; signing out clears them
 * only once the server has said the sign-out happened.
 */
let memOwner: number | null = null;
function pendingOwner(): number | null {
  try {
    const raw = sessionStorage.getItem(OWNER_KEY);
    if (raw !== null && /^\d+$/.test(raw)) return Number(raw);
  } catch {
    /* storage off */
  }
  return memOwner;
}
function setPendingOwner(id: number | null): void {
  memOwner = id;
  try {
    if (id === null) sessionStorage.removeItem(OWNER_KEY);
    else sessionStorage.setItem(OWNER_KEY, String(id));
  } catch {
    /* storage off */
  }
}
function confirmOwner(userId: number): void {
  const owner = pendingOwner();
  if (owner !== null && owner !== userId) clearPendingOps();
  setPendingOwner(userId);
}
/** Signed out, or someone else signed in: one person's unresolved operations are not the next person's. */
export function clearPendingOps(): void {
  memPending = {};
  memJournal = [];
  try {
    sessionStorage.removeItem(PENDING_KEY);
    sessionStorage.removeItem(JOURNAL_KEY);
  } catch {
    /* nothing kept there */
  }
}

/*
 * Which answers end an operation's id. A success, and the four codes that
 * are about the operation itself: done and its answer lost
 * (`replayed_refresh`, `replayed_secret`), or the id belongs to something
 * else (`op_id_reused`, `op_unverifiable`). Nothing else does — not a
 * refusal, not a lapsed login, not the ledger saying "not done yet": an
 * attempt sent earlier under the same id may still be on its way to
 * committing (fifth review R5-F-01: a slow original committed after a
 * retry was refused and the ledger, asked in between, said "not done").
 * Keeping the id costs nothing: if the operation never happened, the next
 * press of the same thing runs it under that id like any new one; if it
 * did, the server answers with it. The id lapses after 30 minutes.
 */
const SETTLING_CODES = new Set(['replayed_refresh', 'replayed_secret', 'op_id_reused', 'op_unverifiable']);
const settles = (e: unknown): boolean => e instanceof ApiError && SETTLING_CODES.has(e.code);

/*
 * One request at a time per identical request in this page (R4-F-02). A
 * second identical call while the first is out is the same operation — it
 * carries the same id — so it gets the same answer rather than racing the
 * first to settle or overwrite the pending entry. "Identical" is the whole
 * request, body included, held in memory only for as long as the request
 * is out — not the stored fingerprint, which leaves a password out and so
 * would make two different password changes look like one (fifth review
 * R5-F-03). Two different password changes at once go to the server on one
 * id, and the second is refused as reused.
 */
const inFlight = new Map<string, Promise<unknown>>();

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  if (!isMutation(init) || /^\/api\/auth\//.test(path)) return attempt<T>(path, init);
  await upgraded;
  const body = typeof init?.body === 'string' ? init.body : '';
  const same = `${init!.method} ${path}\n${body}`;
  const running = inFlight.get(same);
  if (running) return running as Promise<T>;
  const p = fingerprint(init!.method!, path, body)
    .then((key) => send<T>(path, init!, key))
    .finally(() => inFlight.delete(same));
  inFlight.set(same, p);
  return p;
}

async function send<T>(path: string, init: RequestInit, key: string): Promise<T> {
  const pending = readPending();
  const opId = pending[key]?.opId ?? newOpId();
  pending[key] = { opId, at: Date.now() };
  writePending(pending);
  const headers = { ...(init.headers as Record<string, string> | undefined), 'x-op-id': opId };
  const withId = { ...init, headers };
  for (let i = 0; ; i++) {
    try {
      const out = await attempt<T>(path, withId);
      settle(key, opId);
      return out;
    } catch (e) {
      const transient =
        e instanceof TypeError ||
        (e instanceof ApiError &&
          (e.status === 502 || e.status === 504 || e.code === 'uncertain' || e.code === 'bad_reply'));
      if (!transient) {
        if (settles(e)) settle(key, opId);
        else keep(key, opId);
        throw e;
      }
      if (i >= RETRY_DELAYS_MS.length) {
        /* Still unresolved: the entry stays (its clock restarted), so the
           next press of the same thing re-sends this id. */
        keep(key, opId);
        throw new ApiError(0, 'uncertain', undefined, e instanceof ApiError ? e.serverMessage : undefined);
      }
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[i]));
    }
  }
}

async function attempt<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    // Session lives in an httpOnly cookie; it must ride along on every call.
    credentials: 'include',
    ...init,
    /* The content type is declared only when there is something to declare.
       This used to be unconditional, and it broke the one request in the
       product that sends no body: sign-out. `fetch` gives a bodyless POST
       `Content-Length: 0`, and Fastify, seeing `application/json` over an
       empty body, refuses the request with FST_ERR_CTP_EMPTY_JSON_BODY — in
       the content-type parser, so the route handler never runs and the
       session cookie is never cleared. The page then reloaded still signed
       in, which is precisely how it was reported: "it doesn't let me logout,
       just refreshes". verify:http pins Fastify's half of that.

       `...init` moved above this line at the same time. It was below, so a
       caller passing its own `headers` replaced the whole object rather than
       adding to it; nothing does yet, and that is the only reason the header
       above ever arrived at all. */
    headers: {
      ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    let code = `http_${res.status}`;
    let retryAfter: number | undefined;
    let serverMessage: string | undefined;
    try {
      const body = (await res.json()) as { error?: string; retryAfter?: unknown; message?: unknown };
      if (body?.error) code = body.error;
      if (typeof body?.retryAfter === 'number' && Number.isFinite(body.retryAfter)) {
        retryAfter = body.retryAfter;
      }
      if (typeof body?.message === 'string' && body.message) serverMessage = body.message;
    } catch {
      /* non-JSON error body — keep the status code */
    }
    throw new ApiError(res.status, code, retryAfter, serverMessage);
  }
  /* A success whose body cannot be read — cut off on the way, or not JSON —
     is not a failure: for a write the change may well be saved. It is
     retried with the same id like a lost answer, and the server hands the
     first answer back (third review F-01). */
  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiError(res.status, 'bad_reply');
  }
}

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '' || v === false) continue;
    if (Array.isArray(v)) {
      // Fastify parses repeated keys into an array, which is what the
      // Zod schema for multi-value filters expects.
      for (const item of v) sp.append(k, String(item));
    } else {
      sp.set(k, String(v));
    }
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/* ---- WhatsApp (docs/WHATSAPP.md) ---- */
export interface WaNotice {
  kind: string;
  state: 'claimed' | 'sent' | 'logged' | 'skipped' | 'failed';
  attempts: number;
  waId: string | null;
  error: string | null;
  at: string;
}
export interface WaMessage {
  id: number;
  reservationId: number | null;
  phone: string;
  direction: 'in' | 'out';
  kind: string;
  body: string;
  waId: string | null;
  status: string;
  error: string | null;
  userId: number | null;
  at: string;
  statusAt: string | null;
}
export interface WaConsent {
  reservationId: number;
  phone: string;
  granted: boolean;
  source: string;
  wording: string;
  userId: number | null;
  at: string;
  done: boolean;
}
export interface WaBookingView {
  mode: 'off' | 'log' | 'send';
  enabled: boolean;
  phone: string | null;
  consent: WaConsent | null;
  /** The booking's number differs from the consented one: nothing is sent until a new consent. */
  phoneChanged: boolean;
  notices: WaNotice[];
  messages: WaMessage[];
  windowUntil: string | null;
  canWrite: boolean;
}
/** One row of the chat page: a number, its last message, the booking it belongs to. */
export interface WaChat {
  phone: string;
  phoneShown: string;
  reservationId: number | null;
  name: string | null;
  body: string;
  direction: 'in' | 'out';
  kind: string;
  at: string;
  /** The customer's messages still waiting for an answer (0 = answered). */
  waiting: number;
  lastInboundAt: string | null;
  windowUntil: string | null;
}
/** A booking's photos from the old site's uploads (30 Oct 2026). */
export interface ReservationPhoto {
  file: string;
  kind: string;
  n: number;
  at: string | null;
  url: string;
}
export interface ReservationPhotos {
  available: boolean;
  photos: ReservationPhoto[];
}
export interface WaChatList {
  mode: 'off' | 'log' | 'send';
  canWrite: boolean;
  /** The watchdog stopped automatic sending (30 Oct 2026); null = running. */
  paused?: { at: string; reason: string } | null;
  chats: WaChat[];
}
export interface WaChatView {
  mode: 'off' | 'log' | 'send';
  canWrite: boolean;
  phone: string;
  phoneShown: string;
  reservationId: number | null;
  name: string | null;
  messages: WaMessage[];
  windowUntil: string | null;
  /** Translation is configured on the server (31 Oct 2026). */
  translate?: boolean;
  /** The customer's likely language from the number (before any message of theirs says it). */
  phoneLang?: string;
}
export interface WaTranslation {
  text: string;
  /** Detected source language, two letters ('' = unknown). */
  source: string;
}
export interface WaInboxItem {
  reservationId: number | null;
  phone: string;
  phoneShown: string;
  body: string;
  at: string;
  count: number;
}

/* The partners' own pages (4 Nov 2026): the partner API's shapes, as far as the screens use them. */
export interface PartnerRow {
  id: number;
  uuid: string;
  arrival: string;
  departure: string;
  type: string;
  status: string;
  driverName: string;
  spz: string;
  price: number;
  reference: string;
  created: string;
}
export interface PartnerBookingBody {
  arrival: string;
  departure: string;
  parking_type: 'outdoor' | 'garage' | 'vip';
  customer?: { name: string; email: string; phone_prefix: string; phone_number: string };
  vehicle_type?: 'car' | 'SUV' | 'van';
  vehicle_description?: string;
  license_plate?: string;
  flight_number?: string;
  partner_reference?: string;
  note?: string;
  currency?: 'CZK';
  language?: 'cs' | 'en' | 'de';
}
export interface PartnerQuote {
  [k: string]: unknown;
  price?: { total?: number; parking?: number; currency?: string } | number;
}
export interface PartnerBooking {
  [k: string]: unknown;
  id?: string;
  reservation_number?: number;
}

export const api = {
  /** Which database this build is reading — the footer's one question. */
  health: () =>
    request<{ ok: boolean; dialect: string; transport: 'bridge' | 'direct'; readOnly: boolean; env: string; writeActions: 'all' | string[]; keycafe?: 'off' | 'listen' | 'write'; keycafeKeys?: boolean; googleAds?: boolean; whatsapp?: 'off' | 'log' | 'send' }>('/api/health'),

  /* Signing back in as the same person (a session that ran out) keeps that
     person's unresolved operations — they are theirs to finish. Anybody
     else starts clean (third review F-04, fourth review R4-F-01). */
  login: async (body: LoginRequest) => {
    const user = await request<SessionUser>('/api/auth/login', { method: 'POST', body: JSON.stringify(body) });
    confirmOwner(user.id);
    return user;
  },

  /* Unresolved operations are forgotten only once the server has signed
     the person out: a failed sign-out leaves them signed in, and their
     pending ids with them (R4-F-01). */
  logout: async () => {
    const out = await request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' });
    clearPendingOps();
    setPendingOwner(null);
    return out;
  },

  /* The session the server confirms names the owner of this tab's
     unresolved operations, for a tab that never signed in through this
     client (R4-F-01). */
  me: async () => {
    const user = await request<SessionUser>('/api/auth/me');
    confirmOwner(user.id);
    return user;
  },

  /**
   * The whole dashboard, optionally scoped to one yard.
   *
   * `site` goes through `qs`, which drops undefined, so the unfiltered call is
   * byte for byte the URL it has always been — no `?site=` appears until a
   * chip is pressed, and the query cache keys the two apart.
   */
  dashboard: (site?: string, shift?: number) =>
    request<DashboardStats>(`/api/dashboard${qs({ site, shift: shift ? String(shift) : undefined })}`),

  /**
   * The legacy board's silent arrival: mark arrived every complete self-drive
   * booking whose time has come in the live shift (the server picks the
   * window and the rows; nothing is sent). Asked for only after a board
   * answered `autoVerifyDue > 0`, so a quiet board costs nothing. Idempotent
   * by nature — a second call finds nothing left to do.
   */
  autoVerify: () => request<AutoVerifyReceipt>('/api/dashboard/auto-verify', { method: 'POST', body: '{}' }),

  /**
   * The cars standing in one zone of the occupancy panel.
   *
   * Fetched on click rather than shipped with `dashboard()` above, which is
   * the whole reason it is a separate call: the full answer is around fourteen
   * hundred rows spread over forty zones, the dispatcher wants one of them,
   * and the dashboard refreshes every sixty seconds whether anybody clicked or
   * not. Sending all of it to serve a click that usually does not come is the
   * wrong trade on an office line.
   *
   * `zone` is the raw `parking` string, not the label printed on the row —
   * except for `NO_ZONE`, which the read model recognises and turns back into
   * "the column is blank" so that card opens like the rest.
   */
  /**
   * The occupancy outlook — sixty days of expected on-site counts. Fetched
   * only when the Obsazenost tab is open, and cached for five minutes there:
   * it moves with bookings, not with the shift, and nobody refreshes a
   * six-week forecast every minute.
   */
  outlook: () => request<DashboardOutlook>('/api/dashboard/outlook'),

  occupancyZone: (params: Record<string, unknown>) =>
    request<ZoneCars>(`/api/dashboard/occupancy${qs(params)}`),

  reservations: (params: Record<string, unknown>) =>
    request<ReservationPage>(`/api/reservations${qs(params)}`),

  // The id is put through Number() rather than interpolated as given: this
  // value arrives from the URL bar, and a path segment is the one place a
  // string can walk out of the intended route.
  reservation: (id: number) => request<ReservationRow>(`/api/reservations/${Number(id)}`),
  /** The change history — the legacy journal, newest first. */
  journal: (id: number) => request<ReservationJournal>(`/api/reservations/${Number(id)}/journal`),
  photos: (id: number) => request<ReservationPhotos>(`/api/reservations/${Number(id)}/photos`),
  /** The handover protocol: the reservation plus its service checklist. */
  protocol: (id: number) => request<ProtocolData>(`/api/reservations/${Number(id)}/protocol`),

  transfers: () => request<TransferQueues>('/api/transfers'),

  /** Přílety: today's and tomorrow's returns with the airport's landing times. */
  arrivals: () => request<ArrivalsResponse>('/api/arrivals'),

  /**
   * One queue, optionally narrowed to a single yard.
   *
   * `transfers()` above already returns both queues whole, and the tab strip
   * switches between them without a request. This is the call the SITE tabs
   * make, and it is a real round-trip on purpose: the payload above stops at
   * a hundred rows per direction, so filtering it in the browser would show
   * "Z POPu 91" over a list of forty and the missing fifty-one would be the
   * ones furthest down the queue — exactly the cars nobody has looked at.
   */
  transferQueue: (params: Record<string, unknown>) =>
    request<TransferRow[]>(`/api/transfers/queue${qs(params)}`),

  balances: (params: Record<string, unknown>) =>
    request<BalancePage>(`/api/balances${qs(params)}`),

  balance: (id: number) => request<BalanceRow>(`/api/balances/${Number(id)}`),

  clients: (params: Record<string, unknown>) => request<ClientPage>(`/api/clients${qs(params)}`),

  client: (id: number) => request<ClientDetail>(`/api/clients/${Number(id)}`),

  stats: (params: Record<string, unknown>) => request<Stats>(`/api/stats${qs(params)}`),
  /** Sestavy — the three legacy reports on the Statistiky screen. */
  report: (kind: ReportKind, params: Record<string, unknown>) => request<Report>(`/api/reports/${kind}${qs(params)}`),
  sold: (params: Record<string, unknown>) => request<SoldReport>(`/api/reports/sold${qs(params)}`),
  ads: (params: { from: string; to: string }) => request<AdsReport>(`/api/reports/ads${qs(params)}`),
  demand: (params: { from: string; to: string }) => request<DemandReport>(`/api/reports/demand${qs(params)}`),
  demandInsights: (params: { from: string; to: string; channel: string }) => request<DemandInsights>(`/api/reports/demand/insights${qs(params)}`),
  hours: (params: { from: string; to: string; rate: number }) => request<HoursReport>(`/api/reports/hours${qs(params)}`),
  usage: (params: { from: string; to: string }) => request<UsageReport>(`/api/reports/usage${qs(params)}`),
  autotarget: (params: { from: string; to: string }) => request<AutoTargetReport>(`/api/reports/autotarget${qs(params)}`),
  setCommission: (body: CommissionUpdate) =>
    request<{ updated: true }>('/api/reports/sold/commission', { method: 'POST', body: JSON.stringify(body) }),

  staff: () => request<StaffMember[]>('/api/catalog/staff'),
  /** The Partner filter's option list: every `user.status = 'partner'` account. */
  partners: () => request<StaffMember[]>('/api/catalog/partners'),

  /** The spot vocabulary, for a datalist: the option lists plus every label seen in the data. */
  spots: () =>
    request<{ europort: Array<{ value: string; label: string }>; tuchomerice: string[]; used: string[] }>(
      '/api/catalog/spots',
    ),

  /* ---- writes (Phase 1) ---- */

  /** "Přijel": mark the car arrived, optionally with its spot and KeyCafe box. */
  /** "Telefonicky ověřen": the customer's arrival confirmed by phone (legacy q_called). */
  markCalled: (id: number) =>
    request<WriteReceipt>(`/api/reservations/${Number(id)}/called`, { method: 'POST', body: '{}' }),

  arrive: (id: number, body: ArriveRequest) =>
    request<WriteReceipt>(`/api/reservations/${Number(id)}/arrive`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** "Zaplaceno": money taken at the desk, the booking stays open (the owner's fix, 20 October 2026). */
  pay: (id: number, body: PaymentRequest) =>
    request<WriteReceipt>(`/api/reservations/${Number(id)}/payment`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** "Odjel": close the reservation, optionally taking money at the desk. */
  depart: (id: number, body: DepartRequest) =>
    request<WriteReceipt>(`/api/reservations/${Number(id)}/depart`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** "Služba hotová": the job on this reservation is done. */
  serviceDone: (id: number, serviceId: number) =>
    request<WriteReceipt>(`/api/reservations/${Number(id)}/services/${Number(serviceId)}/done`, {
      method: 'POST',
      body: '{}',
    }),
  /** The services checklist of a booking, with the booking's own prices. */
  reservationServices: (id: number) => request<ReservationServices>(`/api/reservations/${Number(id)}/services`),
  /** The booking's KeyCafe card: code, our key if any, the door openings. */
  keycafe: (id: number) => request<KeycafeBookingView>(`/api/reservations/${Number(id)}/keycafe`),
  /** "Vytvořit klíč v KeyCafe": the key and its code, made by the system. */
  createKeycafeKey: (id: number) =>
    request<{ id: number; keyId: number; code: string; codes?: { dropoff: string; pickup: string; service: string }; name: string; at: string }>(`/api/reservations/${Number(id)}/keycafe-key`, {
      method: 'POST',
      body: '{}',
    }),
  /** The booking's WhatsApp card: consent, the three notices, the conversation. */
  whatsapp: (id: number) => request<WaBookingView>(`/api/reservations/${Number(id)}/whatsapp`),
  setWhatsAppConsent: (id: number, granted: boolean) =>
    request<WaConsent>(`/api/reservations/${Number(id)}/whatsapp/consent`, { method: 'POST', body: JSON.stringify({ granted }) }),
  whatsAppReply: (id: number, text: string) =>
    request<{ ok: boolean; id: string }>(`/api/reservations/${Number(id)}/whatsapp/reply`, { method: 'POST', body: JSON.stringify({ text }) }),
  whatsAppInbox: () => request<WaInboxItem[]>('/api/whatsapp/inbox'),
  whatsAppChats: () => request<WaChatList>('/api/whatsapp/chats'),
  whatsAppChat: (phone: string) => request<WaChatView>(`/api/whatsapp/chats/${encodeURIComponent(phone)}`),
  whatsAppChatReply: (phone: string, text: string) =>
    request<{ ok: boolean; id: string }>(`/api/whatsapp/chats/${encodeURIComponent(phone)}/reply`, { method: 'POST', body: JSON.stringify({ text }) }),
  whatsAppTranslate: (phone: string, ids: number[], target: string) =>
    request<{ target: string; translations: Record<string, WaTranslation> }>(`/api/whatsapp/chats/${encodeURIComponent(phone)}/translate`, { method: 'POST', body: JSON.stringify({ ids, target }) }),
  whatsAppTranslateDraft: (text: string, target: string) =>
    request<{ text: string; source: string; target: string }>(`/api/whatsapp/translate`, { method: 'POST', body: JSON.stringify({ text, target }) }),
  whatsAppChatLink: (phone: string, reservationId: number) =>
    request<{ ok: boolean; linked: number }>(`/api/whatsapp/chats/${encodeURIComponent(phone)}/link`, { method: 'POST', body: JSON.stringify({ reservationId }) }),
  whatsAppDismiss: (phone: string) => request<{ ok: boolean }>('/api/whatsapp/inbox/dismiss', { method: 'POST', body: JSON.stringify({ phone }) }),
  /** "Doplňkové služby": the checklist on an existing booking, saved whole. */
  setServices: (id: number, body: ServicesRequest) =>
    request<ServicesReceipt>(`/api/reservations/${Number(id)}/services`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** "Opravit údaje": the free-text fields of the booking. */
  edit: (id: number, body: EditRequest) =>
    request<WriteReceipt>(`/api/reservations/${Number(id)}/edit`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Nastavení → Kapacity. */
  capacitySettings: () => request<CapacitySetting[]>('/api/settings/capacity'),
  saveCapacitySettings: (body: CapacityUpdate) =>
    request<CapacitySetting[]>('/api/settings/capacity', { method: 'POST', body: JSON.stringify(body) }),
  /** Klub → body: a manual ledger line, and taking one away. */
  addClubPoints: (id: number, body: ClubPointCreate) =>
    request<ClubPointRow>(`/api/clients/${Number(id)}/points`, { method: 'POST', body: JSON.stringify(body) }),
  deleteClubPoints: (id: number, pointId: number) =>
    request<{ id: number }>(`/api/clients/${Number(id)}/points/${Number(pointId)}/delete`, { method: 'POST', body: '{}' }),
  /** Uživatelé — the legacy users list and account form. */
  users: (params: Record<string, unknown>) => request<UserList>(`/api/users${qs(params)}`),
  user: (id: number) => request<UserDetail>(`/api/users/${Number(id)}`),
  createUser: (body: UserCreate) => request<UserCreated>('/api/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (id: number, body: UserUpdate) =>
    request<UserDetail>(`/api/users/${Number(id)}`, { method: 'POST', body: JSON.stringify(body) }),
  resetPassword: (id: number) => request<UserCreated>(`/api/users/${Number(id)}/reset-password`, { method: 'POST', body: '{}' }),
  account: () => request<UserDetail>('/api/account'),
  updateAccount: (body: AccountUpdate) => request<UserDetail>('/api/account', { method: 'POST', body: JSON.stringify(body) }),
  changePassword: (body: PasswordChange) =>
    request<{ id: number }>('/api/account/password', { method: 'POST', body: JSON.stringify(body) }),
  /** Nastavení → Dynamické ceny. */
  pricing: () => request<PricingConfig>('/api/settings/pricing'),
  savePricingSettings: (body: PricingSettingsUpdate) => request<PricingSettings>('/api/settings/pricing', { method: 'POST', body: JSON.stringify(body) }),
  importPricing: () =>
    request<{ baseSeasonId: number; winterSeasonIds: number[]; discounts: number; partners: number; retired: number; dated: number; skipped: string[] }>(
      '/api/settings/pricing/import',
      { method: 'POST', body: '{}' },
    ),
  resetPricing: () => request<{ cleared: string[] }>('/api/settings/pricing/reset', { method: 'POST', body: '{}' }),
  createSeason: (body: PricingSeasonCreate) => request<PricingSeason>('/api/settings/pricing/seasons', { method: 'POST', body: JSON.stringify(body) }),
  updateSeason: (id: number, body: PricingSeasonUpdate) =>
    request<PricingSeason>(`/api/settings/pricing/seasons/${Number(id)}`, { method: 'POST', body: JSON.stringify(body) }),
  saveBands: (body: PricingBandsUpdate) => request<PricingBand[]>('/api/settings/pricing/bands', { method: 'POST', body: JSON.stringify(body) }),
  saveLeads: (body: PricingLeadsUpdate) => request<PricingLead[]>('/api/settings/pricing/leads', { method: 'POST', body: JSON.stringify(body) }),
  createDiscount: (body: PricingDiscountUpdate) => request<PricingDiscount>('/api/settings/pricing/discounts', { method: 'POST', body: JSON.stringify(body) }),
  updateDiscount: (id: number, body: PricingDiscountUpdate) =>
    request<PricingDiscount>(`/api/settings/pricing/discounts/${Number(id)}`, { method: 'POST', body: JSON.stringify(body) }),
  /** Nastavení → Ceníky: the legacy lists with their 96 columns. */
  pricelistsFull: () => request<PricelistFull[]>('/api/settings/pricelists'),
  pricelistFull: (id: number) => request<PricelistFull>(`/api/settings/pricelists/${Number(id)}`),
  createPricelist: (body: PricelistCreate) => request<PricelistFull>('/api/settings/pricelists', { method: 'POST', body: JSON.stringify(body) }),
  updatePricelist: (id: number, body: PricelistUpdate) =>
    request<PricelistFull>(`/api/settings/pricelists/${Number(id)}`, { method: 'POST', body: JSON.stringify(body) }),
  addPeriod: (id: number, body: PeriodCreate) =>
    request<PricelistFull>(`/api/settings/pricelists/${Number(id)}/periods`, { method: 'POST', body: JSON.stringify(body) }),
  deletePeriod: (id: number, periodId: number) =>
    request<PricelistFull>(`/api/settings/pricelists/${Number(id)}/periods/${Number(periodId)}/delete`, { method: 'POST', body: '{}' }),
  /** Nastavení → Uzavírky: closed days from `od` (today by default) on. */
  closures: (od?: string) => request<Closure[]>(`/api/settings/closures${od ? `?od=${encodeURIComponent(od)}` : ''}`),
  setClosure: (body: ClosureUpdate) =>
    request<Closure | { day: string; cleared: true }>('/api/settings/closures', { method: 'POST', body: JSON.stringify(body) }),
  /** Nastavení → Služby: the whole catalogue, editing a row, adding one. */
  allServices: () => request<ServiceCatalogFull[]>('/api/settings/services'),
  updateService: (id: number, body: ServiceUpdate) =>
    request<ServiceCatalogFull>(`/api/settings/services/${Number(id)}`, { method: 'POST', body: JSON.stringify(body) }),
  createService: (body: ServiceUpdate) =>
    request<ServiceCatalogFull>('/api/settings/services', { method: 'POST', body: JSON.stringify(body) }),
  /** The gates: the last 100 LPR events (INTERFACE.md §4). */
  lprEvents: () => request<LprEventRow[]>('/api/lpr/events'),
  /** Interní zprávy: the board, and posting / taking down. */
  messages: () => request<InternalMessage[]>('/api/messages'),
  postMessage: (body: MessageCreate) =>
    request<{ id: number; at: string }>('/api/messages', { method: 'POST', body: JSON.stringify(body) }),
  deleteMessage: (id: number) =>
    request<{ id: number; at: string }>(`/api/messages/${Number(id)}/delete`, { method: 'POST', body: '{}' }),
  /** Phase 1b: the catalogue the booking form needs, and the quote. */
  pricelists: () => request<PricelistSummary[]>('/api/catalog/pricelists'),
  services: () => request<ServiceCatalogItem[]>('/api/catalog/services'),
  quote: (q: QuoteQuery) => {
    const p = new URLSearchParams();
    p.set('arrival', q.arrival);
    p.set('departure', q.departure);
    p.set('type', q.type);
    if (q.pricelistId) p.set('pricelistId', String(q.pricelistId));
    if (q.promo) p.set('promo', q.promo);
    if (q.dynamic === false) p.set('dynamic', 'false');
    if (q.partnerId) p.set('partnerId', String(q.partnerId));
    if (q.engine) p.set('engine', q.engine);
    return request<Quote>(`/api/quote?${p.toString()}`);
  },
  /** "Nepřijel" / "Zrušit". */
  setStatus: (id: number, body: StatusRequest) =>
    request<WriteReceipt>(`/api/reservations/${Number(id)}/status`, { method: 'POST', body: JSON.stringify(body) }),
  /** GoPay / BitPay / Fakturace CK amounts — admin. */
  setPrepaid: (id: number, body: PrepaidRequest) =>
    request<WriteReceipt>(`/api/reservations/${Number(id)}/prepaid`, { method: 'POST', body: JSON.stringify(body) }),
  /** "Změnit termín": dates, product, price on an existing booking. */
  reschedule: (id: number, body: RescheduleRequest) =>
    request<RescheduleReceipt>(`/api/reservations/${Number(id)}/reschedule`, { method: 'POST', body: JSON.stringify(body) }),
  /** Dobropis: issue or re-read the credit note for a booking. */
  creditNote: (id: number, kind: CreditNoteKind) =>
    request<CreditNoteData>(`/api/reservations/${Number(id)}/credit-note`, { method: 'POST', body: JSON.stringify({ kind }) }),
  /** Účtenka: issue or re-read the receipt for a booking. */
  receipt: (id: number) => request<ReceiptData>(`/api/reservations/${Number(id)}/receipt`, { method: 'POST', body: '{}' }),
  /** "Nová rezervace": the desk's booking form. */
  /* The partners' own pages (4 Nov 2026). */
  partnerMe: () => request<{ name: string; pricelistId: number | null }>('/api/partner-portal/me'),
  partnerReservations: (from: string, to: string) =>
    request<{ rows: PartnerRow[] }>(`/api/partner-portal/reservations?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  partnerReservation: (id: number) => request<PartnerBooking>(`/api/partner-portal/reservations/${id}`),
  partnerPricelist: () => request<{ id: number; title: string; columns: { cheap: Array<number | null>; outside: Array<number | null>; inside: Array<number | null> } }>('/api/partner-portal/pricelist'),
  partnerQuote: (body: PartnerBookingBody) => request<PartnerQuote>('/api/partner-portal/quote', { method: 'POST', body: JSON.stringify(body) }),
  partnerCreate: (body: PartnerBookingBody) => request<PartnerBooking>('/api/partner-portal/reservations', { method: 'POST', body: JSON.stringify(body) }),
  partnerCancel: (id: number) => request<{ id: number; already: boolean }>(`/api/partner-portal/reservations/${id}/cancel`, { method: 'POST', body: '{}' }),
  createReservation: (body: CreateRequest) =>
    request<CreateReceipt>('/api/reservations', { method: 'POST', body: JSON.stringify(body) }),
  /** "Uzavřít směnu": one balance row and its roster. */
  closeShift: (body: CloseShiftRequest) =>
    request<CloseShiftReceipt>('/api/shifts/close', { method: 'POST', body: JSON.stringify(body) }),
  /** "Přidělit převoz": a driver, a destination, a GPS unit, for one leg. */
  dispatch: (id: number, body: DispatchRequest) =>
    request<WriteReceipt>(`/api/transfers/${Number(id)}/dispatch`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};
